import React, { useMemo, useState } from "react";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import { offsetDateForBrowser, offsetDateForServer } from "../../../shared/timeZoneLogic";
import { useSeriesShowContext } from "../SeriesShowApp";
import { Button } from "@opendate-app/setlist";

const POPUP_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: 0,
  width: "min(440px, calc(100vw - 40px))",
  textAlign: "left",
};

const FLATPICKR_FORMAT = "n/j/y at h:iK";

const AddShowModal = ({ open, onClose }) => {
  const { series, handleAddShow } = useSeriesShowContext();
  const sourceDefault = offsetDateForBrowser(series.source_start_time, series.venue_time_zone);
  const [datetime, setDatetime] = useState(sourceDefault);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const warnings = useMemo(() => {
    const list = [];
    if (datetime) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (datetime < today) {
        list.push("This date is in the past.");
      }
    }
    return list;
  }, [datetime]);

  const reset = () => {
    setDatetime(sourceDefault);
    setError(null);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (submitting || !datetime) return;
    try {
      setSubmitting(true);
      setError(null);
      await handleAddShow(offsetDateForServer(datetime, series.venue_time_zone));
      close();
    } catch (e) {
      const apiErrors = e && e.response && e.response.data && e.response.data.errors;
      const serverError = apiErrors && ((apiErrors.start_time && apiErrors.start_time[0]) || (apiErrors.base && apiErrors.base[0]));
      setError(serverError || "Could not add this show. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup open={open} modal className="popup-modal" contentStyle={POPUP_STYLE} onClose={close} closeOnDocumentClick>
      <div className="popup-modal-container">
        <div className="row m-0 series-confirm-header">
          <div className="col series-confirm-header-col">
            <p className="mb-0 series-confirm-title"><strong>Add an event</strong></p>
          </div>
          <div className="col-xs-auto d-flex align-items-center">
            <a href="#" className="text-muted series-confirm-close" onClick={(e) => { e.preventDefault(); close(); }}>
              <i className="fa fa-times" />
            </a>
          </div>
        </div>
        <div className="row m-0">
          <div className="col-12 series-confirm-body">
            <p className="text-muted mb-3">
              Add an event on a new date and time. It inherits the series defaults from the source event's current setup.
            </p>

            <div className="mb-3">
              <label className="form-label"><strong>Date &amp; time</strong></label>
              <div className="flatpickr-date-input px-2 d-flex align-items-center">
                <div className="pr-2"><i className="far fa-calendar" /></div>
                <Flatpickr
                  options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT }}
                  placeholder="Select date / time"
                  value={datetime || ""}
                  onClose={([d]) => setDatetime(d)}
                />
              </div>
            </div>

            {warnings.length > 0 && (
              <div className="alert alert-warning py-2">
                {warnings.map((msg, i) => <div key={i}>{msg}</div>)}
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2">{error}</div>
            )}

            <div className="d-flex justify-content-end series-confirm-actions">
              <Button variant="secondary" onClick={close} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={submit} disabled={submitting || !datetime}>
                {submitting ? "Adding…" : "Add Event"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default AddShowModal;
